import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  inquiryApi,
  socialApi,
  isApiError,
  InvalidApiResponseError,
  assertMessageAcknowledgement,
  type InquiryApi,
  type CoachingProposal,
} from "@bw/shared/api";
import {
  useAuth,
  useMessageReadBoundary,
  useCoachingInquiry,
  useCoachingInquiryOffers,
  useCreateCoachingFromInquiry,
  useCoachingRequest,
} from "@bw/shared/hooks";
import { AssigneePicker } from "./assignee-picker";
import { CoachingProposalComposer } from "../coaching/coaching-proposal-composer";
import { BusinessProposalReview } from "../coaching/coaching-workspace-inbox";

type Transition = Parameters<InquiryApi["transition"]>[1]["action"];
type MessageCommand = Parameters<typeof socialApi.sendMessage>[1];
type InquiryMessage = Awaited<ReturnType<typeof socialApi.getMessages>>["items"][number];
type TimelineEntry =
  | { kind: "message"; id: string; at: string; message: InquiryMessage }
  | { kind: "proposal"; id: string; at: string; proposal: CoachingProposal }
  | { kind: "resolution"; id: string; at: string; proposal: CoachingProposal };
type UploadDescriptor = Awaited<
  ReturnType<typeof socialApi.uploadMessageAttachment>
>;
type UploadDraft = {
  key: string;
  file: File;
  status: "uploading" | "failed" | "ready";
  progress: number;
  descriptor?: UploadDescriptor;
  error?: string;
};
const StableTimeline = memo(function StableTimeline({ children }: {
  children: ReactNode;
  entries: TimelineEntry[];
  canDeleteOwn: boolean;
  canModerate: boolean;
  updating: boolean;
  onDelete: (id: string) => void;
}) { return <>{children}</>; }, (previous, next) =>
  previous.entries === next.entries &&
  previous.canDeleteOwn === next.canDeleteOwn &&
  previous.canModerate === next.canModerate &&
  previous.updating === next.updating &&
  previous.onDelete === next.onDelete);
const messageSchema = z.object({
  message: z.string().trim().max(4000),
});
const offerDraftKey = (actorId: string | undefined, conversationId: string) =>
  `business-coach-offer-draft:${actorId ?? "guest"}:${conversationId}`;
function readOfferDraft(actorId: string | undefined, conversationId: string): string {
  try { return sessionStorage.getItem(offerDraftKey(actorId, conversationId)) ?? ""; }
  catch { return ""; }
}
function saveOfferDraft(actorId: string | undefined, conversationId: string, requestId: string) {
  try { sessionStorage.setItem(offerDraftKey(actorId, conversationId), requestId); }
  catch { /* The current in-memory draft still works. */ }
}
function clearOfferDraft(actorId: string | undefined, conversationId: string) {
  try { sessionStorage.removeItem(offerDraftKey(actorId, conversationId)); }
  catch { /* Storage may be unavailable. */ }
}

export function InquiryThread() {
  const { conversationId = "" } = useParams();
  const { user } = useAuth();
  return (
    <Thread
      key={`${user?.uid}:${conversationId}`}
      conversationId={conversationId}
    />
  );
}

function Thread({ conversationId }: { conversationId: string }) {
  const { user } = useAuth();
  const client = useQueryClient();
  const [cursor, setCursor] = useState<string>();
  const [readDenied, setReadDenied] = useState(false);
  const live = useRef(true);
  const busy = useRef(false);
  const command = useRef<{ fingerprint: string; data: MessageCommand } | null>(
    null,
  );
  const [files, setFiles] = useState<UploadDraft[]>([]);
  const filesRef = useRef<UploadDraft[]>([]);
  const uploading = useRef(new Set<string>());
  const [uploadError, setUploadError] = useState<string | null>(null);
  const provenance = useRef(new Map<string, string | undefined>());
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const metadata = useQuery({
    queryKey: ["business-inquiry", user?.uid, conversationId],
    queryFn: ({ signal }) => inquiryApi.get(conversationId, signal),
    enabled: Boolean(user && conversationId),
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 15_000,
    retry: false,
  });
  const inquiry = metadata.data;
  const coaching = useCoachingInquiry(
    conversationId,
    Boolean(!metadata.isError && inquiry?.target?.kind === "coach"),
  );
  const offers = useCoachingInquiryOffers(conversationId,
    Boolean(!metadata.isError && inquiry?.target?.kind === "coach"));
  const convert = useCreateCoachingFromInquiry();
  const [composeRequestId, setComposeRequestId] = useState(() => readOfferDraft(user?.uid, conversationId));
  const coachingRequest = useCoachingRequest(composeRequestId);
  const [composing, setComposing] = useState(false);
  const createCommand = useRef<{ revision: number; key: string } | null>(null);
  const creating = useRef(false);
  useEffect(() => {
    if (!composeRequestId || !coachingRequest.isError || !isApiError(coachingRequest.error) ||
        ![403, 404].includes(coachingRequest.error.status)) return;
    clearOfferDraft(user?.uid, conversationId);
    setComposeRequestId("");
    setComposing(false);
  }, [composeRequestId, coachingRequest.isError, coachingRequest.error, user?.uid, conversationId]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (inquiry?.target?.kind === "coach" && !composing) void offers.refetch();
      if (composeRequestId) void coachingRequest.refetch();
      void client.invalidateQueries({ queryKey: ["coaching", user?.uid, "proposal"] });
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [inquiry?.target?.kind, composing, offers.refetch, composeRequestId, coachingRequest.refetch, client, user?.uid]);
  const coachingRefetch = coaching.refetch;
  useEffect(() => {
    if (inquiry?.target?.kind === "coach" && !metadata.isError)
      void coachingRefetch();
  }, [
    inquiry?.revision,
    inquiry?.target?.kind,
    metadata.isError,
    coachingRefetch,
  ]);
  const verified = Boolean(
    user &&
      metadata.isFetchedAfterMount &&
      !metadata.isError &&
      inquiry?.conversation_id === conversationId,
  );
  const messagePrefix = useMemo(
    () => ["business-inquiry-messages", user?.uid, conversationId],
    [user?.uid, conversationId],
  );
  const messages = useQuery({
    queryKey: [...messagePrefix, cursor],
    queryFn: ({ signal }) =>
      socialApi.getMessages(conversationId, {
        limit: 50,
        ...(cursor ? { cursor } : {}),
        signal,
      }),
    enabled: verified && !readDenied,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchInterval: 15_000,
    retry: false,
  });
  const refreshMetadata = useCallback(async () => {
    if (live.current) await metadata.refetch();
  }, [metadata.refetch]);
  const clearMessages = useCallback(async () => {
    if (!live.current) return;
    setReadDenied(true);
    await client.cancelQueries({ queryKey: messagePrefix });
    client.removeQueries({ queryKey: messagePrefix });
  }, [client, messagePrefix]);
  const revoke = useCallback(async () => {
    await clearMessages();
    await refreshMetadata();
  }, [clearMessages, refreshMetadata]);
  useEffect(() => {
    if (
      (isApiError(metadata.error) &&
        [401, 403, 404].includes(metadata.error.status)) ||
      (inquiry && inquiry.conversation_id !== conversationId)
    )
      void clearMessages();
  }, [metadata.error, inquiry, conversationId, clearMessages]);
  const retryMetadata = async () => {
    const result = await metadata.refetch();
    const valid =
      result.isSuccess && result.data.conversation_id === conversationId;
    if (live.current && valid) setReadDenied(false);
    return valid;
  };
  useEffect(() => {
    if (
      isApiError(messages.error) &&
      [401, 403, 404].includes(messages.error.status)
    )
      void revoke();
  }, [messages.error, revoke]);
  const batches = useMemo(
    () =>
      messages.data && !messages.isError && messages.isFetchedAfterMount
        ? [messages.data.read_message_ids]
        : [],
    [messages.data, messages.isError, messages.isFetchedAfterMount],
  );
  useEffect(() => {
    for (const batch of batches)
      provenance.current.set(JSON.stringify(batch), cursor);
  }, [batches, cursor]);
  const mark = useMutation({
    mutationFn: (ids: string[]) =>
      socialApi.markConversationRead(conversationId, ids),
    onSuccess: refreshMetadata,
    retry: false,
  });
  const refreshReadPage = useCallback(
    async (ids: string[]) => {
      const source = provenance.current.get(JSON.stringify(ids));
      await client.fetchQuery({
        queryKey: [...messagePrefix, source],
        staleTime: 0,
        queryFn: ({ signal }) =>
          socialApi.getMessages(conversationId, {
            limit: 50,
            ...(source ? { cursor: source } : {}),
            signal,
          }),
      });
    },
    [client, conversationId, messagePrefix],
  );
  const read = useMessageReadBoundary(
    mark,
    `${user?.uid}:${conversationId}`,
    batches,
    verified && !readDenied && !messages.isError,
    refreshReadPage,
    revoke,
  );
  const transition = useMutation({
    mutationFn: (data: Parameters<InquiryApi["transition"]>[1]) =>
      inquiryApi.transition(conversationId, data),
    onSettled: refreshMetadata,
    retry: false,
  });
  const assign = useMutation({
    mutationFn: (data: Parameters<InquiryApi["assign"]>[1]) =>
      inquiryApi.assign(conversationId, data),
    onSettled: refreshMetadata,
    retry: false,
  });
  const remove = useMutation({
    mutationFn: (id: string) => socialApi.deleteMessage(id),
    onSettled: async () => {
      if (live.current) {
        await messages.refetch();
        await refreshMetadata();
      }
    },
    retry: false,
  });
  const form = useForm<z.infer<typeof messageSchema>>({
    resolver: zodResolver(messageSchema),
    defaultValues: { message: "" },
  });
  const send = useMutation({
    mutationFn: async (data: MessageCommand) => {
      const response = await socialApi.sendMessage(conversationId, data);
      assertMessageAcknowledgement(response, conversationId);
      return response;
    },
    onSettled: refreshMetadata,
    retry: false,
  });
  function changeFiles(update: (current: UploadDraft[]) => UploadDraft[]) {
    if (!live.current) return;
    filesRef.current = update(filesRef.current);
    setFiles(filesRef.current);
  }
  async function upload(entry: UploadDraft) {
    if (
      !live.current ||
      !verified ||
      inquiry?.write_access !== "allowed" ||
      readDenied ||
      messages.isError ||
      uploading.current.has(entry.key)
    )
      return;
    uploading.current.add(entry.key);
    changeFiles((current) =>
      current.map((item) =>
        item.key === entry.key
          ? { ...item, status: "uploading", error: undefined }
          : item,
      ),
    );
    try {
      const descriptor = await socialApi.uploadMessageAttachment(
        conversationId,
        entry.file,
        entry.key,
        (percent) => {
          changeFiles((current) =>
            current.map((item) =>
              item.key === entry.key
                ? { ...item, progress: Math.max(0, Math.min(100, percent)) }
                : item,
            ),
          );
        },
      );
      if (
        descriptor.state !== "ready" ||
        typeof descriptor.attachment_id !== "string" ||
        !descriptor.attachment_id
      )
        throw new InvalidApiResponseError("/social/conversations/attachments");
      changeFiles((current) =>
        current.map((item) =>
          item.key === entry.key
            ? { ...item, status: "ready", descriptor, progress: 100 }
            : item,
        ),
      );
    } catch (error) {
      changeFiles((current) =>
        current.map((item) =>
          item.key === entry.key
            ? {
                ...item,
                status: "failed",
                error: isApiError(error)
                  ? error.message
                  : "Upload was not confirmed. Retry or remove the file.",
              }
            : item,
        ),
      );
      await refreshMetadata();
    } finally {
      uploading.current.delete(entry.key);
    }
  }
  function selectFiles(event: ChangeEvent<HTMLInputElement>) {
    const chosen = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = "";
    if (
      !verified ||
      inquiry?.write_access !== "allowed" ||
      busy.current ||
      readDenied ||
      messages.isError
    )
      return;
    if (chosen.length + filesRef.current.length > 4) {
      setUploadError("Attach up to 4 files per message.");
      return;
    }
    setUploadError(null);
    send.reset();
    const entries: UploadDraft[] = chosen.map((file) => ({
      key: crypto.randomUUID(),
      file,
      status: "uploading",
      progress: 0,
    }));
    changeFiles((current) => [...current, ...entries]);
    entries.forEach((entry) => {
      void upload(entry);
    });
  }
  async function submit({ message }: z.infer<typeof messageSchema>) {
    if (
      !verified ||
      inquiry?.write_access !== "allowed" ||
      busy.current ||
      readDenied ||
      messages.isError ||
      !live.current
    )
      return;
    const ready = filesRef.current;
    if (
      uploadError ||
      ready.some((item) => item.status !== "ready" || !item.descriptor)
    )
      return;
    if (!message && ready.length === 0) {
      form.setError("message", { message: "Enter a message or attach a file" });
      return;
    }
    const payload = {
      content: message,
      attachment_ids: ready.flatMap((item) =>
        item.descriptor ? [item.descriptor.attachment_id] : [],
      ),
    };
    const fingerprint = JSON.stringify(payload);
    busy.current = true;
    if (command.current?.fingerprint !== fingerprint)
      command.current = {
        fingerprint,
        data: { ...payload, idempotency_key: crypto.randomUUID() },
      };
    try {
      await send.mutateAsync(command.current.data);
      if (live.current) {
        form.reset();
        command.current = null;
        changeFiles(() => []);
        setUploadError(null);
        setCursor(undefined);
        await messages.refetch();
      }
    } catch {
      /* Keep exact command and draft for deliberate retry. */
    } finally {
      busy.current = false;
    }
  }
  const updating =
    transition.isPending ||
    assign.isPending ||
    remove.isPending ||
    send.isPending;
  const attachmentsReady = files.every((item) => item.status === "ready");
  const draftReady =
    attachmentsReady &&
    !uploadError &&
    (Boolean(form.watch("message").trim()) || files.length > 0);
  const conversationOffers = useMemo(
    () => offers.data?.pages.flatMap((page) => page.items) ?? [],
    [offers.data],
  );
  const timeline = useMemo<TimelineEntry[]>(() => [
    ...(messages.data?.items ?? []).map((message) => ({ kind: "message" as const,
      id: `message-${message.message_id}`, at: message.created_at, message })),
    ...conversationOffers.flatMap((proposal) => {
      const items: TimelineEntry[] = [{ kind: "proposal", id: `proposal-${proposal.revision_id}`,
          at: proposal.issued_at, proposal }];
      if (proposal.resolved_action && proposal.resolved_at) items.push({ kind: "resolution",
        id: `proposal-resolution-${proposal.revision_id}`, at: proposal.resolved_at, proposal });
      return items;
    }),
  ].sort((left, right) => Date.parse(left.at) - Date.parse(right.at) || left.id.localeCompare(right.id)),
  [messages.data?.items, conversationOffers]);
  if (
    metadata.isError ||
    (metadata.data && metadata.data.conversation_id !== conversationId)
  )
    return (
      <Stack spacing={2} sx={{ p: 3 }}>
        <Alert severity="warning">
          This inquiry is unavailable. Your access may have changed.
        </Alert>
        <Button size="small" onClick={() => void retryMetadata()}>
          Retry
        </Button>
        <Button component={RouterLink} to="/inquiries" size="small">
          Back to inquiries
        </Button>
      </Stack>
    );
  if (!verified || !inquiry)
    return (
      <Typography role="status" sx={{ p: 3 }}>
        Loading inquiry…
      </Typography>
    );
  const actionButtons: Array<[Transition, boolean]> = [
    ["close", inquiry.can_close],
    ["reopen", inquiry.can_reopen],
    ["archive", inquiry.can_archive],
    ["reject", inquiry.can_reject],
  ];
  const sourceOffer = conversationOffers.reduce<CoachingProposal | undefined>((latest, item) =>
    !latest || Date.parse(item.issued_at) > Date.parse(latest.issued_at) ? item : latest, undefined);
  return (
    <Stack spacing={2} sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
      <Button
        component={RouterLink}
        to="/inquiries"
        size="small"
        sx={{ alignSelf: "flex-start" }}
      >
        Back to inquiries
      </Button>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap" }}
      >
        <Typography component="h1" variant="h4">
          {inquiry.topic}
        </Typography>
        <Chip size="small" label={inquiry.lifecycle} />
      </Stack>
      <Typography color="text.secondary">
        {inquiry.requester_name ?? "Player"} ·{" "}
        {inquiry.facility_name ?? inquiry.target_name ?? "Coaching inquiry"}
      </Typography>
      {inquiry.assigned_user_id ? (
        <Typography variant="body2">
          {inquiry.assigned_name
            ? `Assigned to ${inquiry.assigned_name}`
            : "Assigned"}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {actionButtons
          .filter(([, allowed]) => allowed === true)
          .map(([action]) => (
            <Button
              key={action}
              size="small"
              disabled={updating}
              onClick={() =>
                transition.mutate({
                  action,
                  expected_revision: inquiry.revision,
                })
              }
            >
              {action[0].toUpperCase() + action.slice(1)}
            </Button>
          ))}
        {inquiry.can_assign === true && user ? (
          <AssigneePicker
            key={`${user.uid}:${conversationId}:${inquiry.revision}`}
            actorId={user.uid}
            conversationId={conversationId}
            revision={inquiry.revision}
            assignedUserId={inquiry.assigned_user_id}
            disabled={updating || metadata.isFetching}
            onAssign={(userId, expectedRevision) =>
              assign.mutate({
                assigned_user_id: userId,
                expected_revision: expectedRevision,
              })
            }
            onAuthorityError={refreshMetadata}
          />
        ) : null}
        {inquiry.can_assign === true && inquiry.assigned_user_id ? (
          <Button
            size="small"
            disabled={updating}
            onClick={() =>
              assign.mutate({
                assigned_user_id: null,
                expected_revision: inquiry.revision,
              })
            }
          >
            Unassign
          </Button>
        ) : null}
      </Stack>
      {transition.error || assign.error || remove.error ? (
        <Alert severity="warning">
          We couldn&apos;t confirm the update. Check the refreshed inquiry
          before trying again.
        </Alert>
      ) : null}
      {convert.isError ? (
        <Alert severity="warning">
          The coaching request could not be confirmed. Refresh the inquiry
          before trying again.
        </Alert>
      ) : null}
      {composeRequestId && coachingRequest.isError ? (
        <Alert severity="warning">Coaching options are unavailable. Refresh this conversation.</Alert>
      ) : null}
      {offers.isError ? (
        <Alert severity="warning" action={<Button size="small" onClick={() => void offers.refetch()}>Retry</Button>}>
          Proposal history is unavailable.
        </Alert>
      ) : null}
      {readDenied || messages.isError ? (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              onClick={() => {
                void retryMetadata().then((valid) => {
                  if (valid && live.current && !readDenied)
                    void messages.refetch();
                });
              }}
            >
              Retry
            </Button>
          }
        >
          Messages are unavailable. Your access may have changed.
        </Alert>
      ) : messages.isPending || !messages.isFetchedAfterMount ? (
        <Typography role="status">Loading messages…</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={1}>
            {offers.hasNextPage ? <Button size="small" disabled={offers.isFetchingNextPage}
              onClick={() => void offers.fetchNextPage()}>Earlier proposals</Button> : null}
            {messages.data?.has_more && messages.data.next_cursor ? (
              <Button
                size="small"
                onClick={() =>
                  setCursor(messages.data?.next_cursor ?? undefined)
                }
              >
                Earlier messages
              </Button>
            ) : null}
            {cursor ? (
              <Button size="small" onClick={() => setCursor(undefined)}>
                Latest messages
              </Button>
            ) : null}
          </Stack>
          <Paper
            aria-label="Inquiry messages"
            component={Stack}
            spacing={1.5}
            variant="outlined"
            sx={{
              minHeight: 220,
              p: { xs: 1.5, sm: 2 },
              bgcolor: "action.hover",
            }}
          >
            <StableTimeline entries={timeline} canDeleteOwn={inquiry.can_delete_own_messages}
              canModerate={inquiry.can_moderate_messages} updating={updating} onDelete={remove.mutate}>
            {timeline.map((entry) => entry.kind === "proposal" ? (
              <Box key={entry.id} id={entry.id} tabIndex={-1} data-testid="inquiry-proposal"
                sx={{ width: "100%", scrollMarginTop: 80 }}>
                <BusinessProposalReview requestId={entry.proposal.request_id} revisionId={entry.proposal.revision_id} embedded />
                {entry.proposal.acceptance_id ? <Button size="small" component={RouterLink}
                  to={`/coaching/${encodeURIComponent(entry.proposal.request_id)}`}>
                  Manage lesson
                </Button> : null}
              </Box>
            ) : entry.kind === "resolution" ? (
              <Typography key={entry.id} role="status" data-testid="inquiry-resolution"
                variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                {({ accept: "Proposal accepted", request_revision: "Changes requested",
                  decline: "Proposal declined", withdraw: "Proposal withdrawn" } as const)[entry.proposal.resolved_action!]}
              </Typography>
            ) : (
              <Box
                key={entry.id}
                data-testid="inquiry-message"
                sx={{
                  p: 1.5,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  width: "fit-content",
                  maxWidth: "min(82%, 42rem)",
                  alignSelf: entry.message.is_own ? "flex-end" : "flex-start",
                  overflowWrap: "anywhere",
                  bgcolor: entry.message.is_own ? "primary.main" : "background.paper",
                  color: entry.message.is_own
                    ? "primary.contrastText"
                    : "text.primary",
                }}
              >
                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", justifyContent: "space-between" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: entry.message.is_own ? "inherit" : "text.secondary",
                      opacity: entry.message.is_own ? 0.82 : 1,
                    }}
                  >
                    {entry.message.is_own ? "You" : entry.message.sender_display_name}
                  </Typography>
                  {!entry.message.is_deleted &&
                  (entry.message.is_own
                    ? inquiry.can_delete_own_messages
                    : inquiry.can_moderate_messages) === true ? (
                    <Tooltip title="Delete message">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Delete message"
                          disabled={updating}
                          onClick={() => remove.mutate(entry.message.message_id)}
                          sx={{ color: "inherit", opacity: 0.8, p: 0.25 }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </Stack>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>
                  {entry.message.is_deleted ? "Message removed" : entry.message.content}
                </Typography>
                {!entry.message.is_deleted && entry.message.shared_post ? (
                  <Typography variant="body2">
                    {entry.message.shared_post.content}
                  </Typography>
                ) : null}
                {!entry.message.is_deleted && entry.message.shared_post_unavailable ? (
                  <Typography variant="body2" color="text.secondary">
                    Shared post unavailable
                  </Typography>
                ) : null}
                {!entry.message.is_deleted
                  ? entry.message.attachments?.map((attachment) =>
                      attachment.state === "attached" ? (
                        <Link
                          key={attachment.attachment_id}
                          href={`/bff/api/social/message-attachments/${encodeURIComponent(attachment.attachment_id)}/content`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {attachment.filename ?? "Attachment"}
                        </Link>
                      ) : (
                        <Typography
                          key={attachment.attachment_id}
                          variant="caption"
                        >
                          Attachment unavailable
                        </Typography>
                      ),
                    )
                  : null}
              </Box>
            ))}
            </StableTimeline>
          </Paper>
        </>
      )}
      {composeRequestId && composing && coachingRequest.data?.can_issue ? (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
          <CoachingProposalComposer requestId={composeRequestId}
            sourceOffer={offers.isFetchedAfterMount && !offers.isError && !offers.isFetching ? sourceOffer : undefined}
            onClose={(sent) => {
            setComposing(false);
            if (sent) {
              setComposeRequestId("");
              clearOfferDraft(user?.uid, conversationId);
            }
            void offers.refetch();
            void coachingRequest.refetch();
          }} />
        </Paper>
      ) : null}
      {read.isError ? (
        <Alert
          severity="warning"
          action={
            <Button size="small" onClick={read.retry}>
              Retry read status
            </Button>
          }
        >
          Read status could not be updated.
        </Alert>
      ) : null}
      <Paper
        component="form"
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 } }}
        onSubmit={form.handleSubmit(submit)}
      >
        <Stack spacing={1}>
          <Stack role="group" aria-label="Conversation actions" direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {!composing && !coaching.isError && coaching.isFetchedAfterMount &&
            coaching.data?.can_create ? (
              <Button type="button" size="small" variant="contained"
                disabled={readDenied || messages.isError || convert.isPending || coaching.isFetching ||
                  Boolean(composeRequestId && (coachingRequest.isLoading || coachingRequest.isError || coachingRequest.isFetching))}
                onClick={() => {
                  if (composeRequestId && coachingRequest.data?.can_issue &&
                      coachingRequest.data.proposal_state === "none") {
                    setComposing(true);
                    return;
                  }
                  if (!coaching.data?.can_create || creating.current) return;
                  creating.current = true;
                  if (createCommand.current?.revision !== inquiry.revision)
                    createCommand.current = { revision: inquiry.revision, key: crypto.randomUUID() };
                  convert.mutate([conversationId, { expected_inquiry_revision: createCommand.current.revision,
                    idempotency_key: createCommand.current.key }], {
                    onSuccess: (created) => {
                      if (!live.current) return;
                      createCommand.current = null;
                      setComposeRequestId(created.request_id);
                      saveOfferDraft(user?.uid, conversationId, created.request_id);
                      setComposing(true);
                      void coaching.refetch();
                    },
                    onSettled: () => { creating.current = false; void refreshMetadata(); },
                  });
                }}>
                {convert.isPending ? "Opening proposal…" : "Create proposal"}
              </Button>
            ) : null}
            <Tooltip title="Attach files" describeChild>
              <span>
                <IconButton
                  component="label"
                  size="small"
                  aria-label="Add attachment"
                  disabled={
                    inquiry.write_access !== "allowed" ||
                    updating ||
                    readDenied ||
                    messages.isError ||
                    files.length >= 4
                  }
                >
                  <AttachFileRoundedIcon fontSize="small" />
                  <input
                    type="file"
                    multiple
                    hidden
                    aria-label="Attach files"
                    onChange={selectFiles}
                    disabled={
                      inquiry.write_access !== "allowed" ||
                      updating ||
                      readDenied ||
                      messages.isError ||
                      files.length >= 4
                    }
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {files.map((entry) => (
            <Stack key={entry.key} spacing={0.5}>
              <Typography variant="body2">{entry.file.name}</Typography>
              {entry.status === "uploading" ? (
                <Typography variant="caption" role="status">
                  Uploading: {Math.round(entry.progress)}%
                </Typography>
              ) : null}
              {entry.error ? (
                <Alert
                  severity="warning"
                  action={
                    <Button
                      size="small"
                      aria-label={`Retry upload ${entry.file.name}`}
                      disabled={
                        inquiry.write_access !== "allowed" ||
                        updating ||
                        readDenied ||
                        messages.isError
                      }
                      onClick={() => {
                        void upload(entry);
                      }}
                    >
                      Retry upload
                    </Button>
                  }
                >
                  {entry.error}
                </Alert>
              ) : null}
              <Button
                size="small"
                aria-label={`Remove ${entry.file.name}`}
                disabled={updating}
                sx={{ alignSelf: "flex-start" }}
                onClick={() => {
                  changeFiles((current) =>
                    current.filter((item) => item.key !== entry.key),
                  );
                  send.reset();
                  setUploadError(null);
                }}
              >
                Remove
              </Button>
            </Stack>
          ))}
          {uploadError ? (
            <Alert
              severity="warning"
              action={
                <Button size="small" onClick={() => setUploadError(null)}>
                  Dismiss
                </Button>
              }
            >
              {uploadError}
            </Alert>
          ) : null}
          <TextField
            label="Message"
            size="small"
            multiline
            minRows={3}
            {...form.register("message", { onChange: () => send.reset() })}
            disabled={
              inquiry.write_access !== "allowed" ||
              updating ||
              readDenied ||
              messages.isError
            }
            error={Boolean(form.formState.errors.message)}
            helperText={form.formState.errors.message?.message}
          />
          {inquiry.write_access !== "allowed" ? (
            <Typography variant="body2" color="text.secondary">
              This inquiry is read-only.
            </Typography>
          ) : null}
          {send.error ? (
            <Alert
              severity="warning"
              action={
                <Button
                  size="small"
                  disabled={
                    inquiry.write_access !== "allowed" ||
                    updating ||
                    readDenied ||
                    messages.isError ||
                    !draftReady
                  }
                  onClick={form.handleSubmit(submit)}
                >
                  Retry
                </Button>
              }
            >
              {isApiError(send.error)
                ? send.error.message
                : "The message was not confirmed. Retry to check it."}
            </Alert>
          ) : null}
          <Button
            type="submit"
            size="small"
            variant="contained"
            sx={{ alignSelf: "flex-end" }}
            disabled={
              inquiry.write_access !== "allowed" ||
              updating ||
              readDenied ||
              messages.isError ||
              !draftReady
            }
          >
            Send
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}
